import { EnumUserType } from "../generated/enums";

// Espaces de l'application. Chaque type d'utilisateur appartient à UN espace ; les assistants
// partagent l'espace de leur type principal (avec, plus tard, des droits réduits — voir isAssistant).
// Le routing FRONT par espace est de l'UX : la vraie sécurité reste côté backend (AuthMiddleware /
// AdminMiddleware / scoping par entreprise & GroupAccess).
export type Space = "admin" | "breeder" | "installer" | "group";

export const TYPE_TO_SPACE: Record<EnumUserType, Space> = {
  admin: "admin",
  psc: "admin",
  breeder: "breeder",
  breeder_assistant: "breeder",
  installer: "installer",
  installer_assistant: "installer",
  group: "group",
  group_assistant: "group",
};

export interface SpaceMeta {
  label: string; // libellé affiché
  home: string; // route d'accueil de l'espace
  accent: string; // couleur d'accent de l'espace (hex : item de nav actif)
}

export const SPACE_META: Record<Space, SpaceMeta> = {
  admin: { label: "Admin", home: "/admin", accent: "#6B7280" }, // gris
  breeder: { label: "Éleveur", home: "/breeder", accent: "#16A34A" }, // vert
  installer: { label: "Installateur", home: "/installer", accent: "#EA580C" }, // orange
  group: { label: "Groupement", home: "/group", accent: "#7C3AED" }, // violet
};

// spaceForType : espace d'un type. Défaut "admin" si type inconnu/absent (ne devrait pas arriver ;
// le backend garde de toute façon les accès).
export const spaceForType = (type?: string | null): Space =>
  (type && TYPE_TO_SPACE[type as EnumUserType]) || "admin";

// landingForType : route d'accueil vers laquelle rediriger un type donné.
export const landingForType = (type?: string | null): string =>
  SPACE_META[spaceForType(type)].home;

// isAssistant : vrai pour les types "*_assistant" (droits réduits dans leur espace, géré plus tard).
export const isAssistant = (type?: string | null): boolean =>
  !!type && type.endsWith("_assistant");

/**
 * Vrai pour un compte admin ou psc (tous deux dans l'espace "admin").
 *
 * Sert à choisir entre une route ADMIN (voit tout) et sa version scopée : les bâtiments
 * (`getAdminBuildings` / `getBuildings`) comme les régulateurs (`getAdminDevices` / `getDevices`)
 * ont ce couple de routes. Lit `accountStorage` directement — utilisable hors composant React,
 * là où `useAccountStorage` n'est pas disponible (couche api/).
 */
export const isAdminAccount = (): boolean => {
  try {
    const raw = localStorage.getItem("accountStorage");
    const type = raw ? (JSON.parse(raw) as { type?: string }).type : undefined;
    return spaceForType(type) === "admin";
  } catch {
    return false;
  }
};

/**
 * Route de connexion à distance (vue noVNC) d'un régulateur, dans l'espace de l'utilisateur.
 * Chaque espace a SA route vers le même composant — `RoleRoute` renverrait un éleveur sur
 * `/breeder` s'il ouvrait celle de l'admin.
 *
 * Renvoie `undefined` quand l'espace n'a pas (encore) de route : installateur et groupement
 * restent à trancher (2026-07-30). L'appelant masque alors l'accès plutôt que de proposer un
 * lien qui renverrait l'utilisateur à son accueil.
 */
export const remoteDeviceRoute = (
  uuid: string,
  type?: string | null
): string | undefined => {
  const space = spaceForType(type);
  if (space === "admin") return `/admin/device/${uuid}`;
  if (space === "breeder") return `/breeder/device/${uuid}`;
  // Installateur et groupement : accès ouvert depuis que le partage de produits existe
  // (CompanyShare). Ils ne voient dans leur liste que les produits qui leur appartiennent
  // ou qu'un client leur a partagés — c'est le backend qui filtre (link.DeviceAccessCondition),
  // donc proposer l'accès ici ne contourne rien.
  if (space === "installer") return `/installer/device/${uuid}`;
  if (space === "group") return `/group/device/${uuid}`;
  return undefined;
};
